import { isDefined, isNotDefined } from '@sgme/fp';
import type { PersistencePayload } from 'apiConnector/dashboard/model';
import { DocumentType } from 'models/documents/DocumentType';
import { createSelector } from 'reselect';
import type { DynamicFormInfos } from 'state/dynamic/infos/infos.model';
import { isLiveQuote } from 'state/quotes/utils';
import { getDocument, getFormInfos, getIsUserInternal } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { StaticFormInfos } from 'state/static/infos/infos.model';
import { addKey } from 'state/utils/collection';

import type { SolvingModeDetail } from '../../details/details.model';
import { getPendingQuoteDetailsMap } from '../../selectors';
import type { DashboardFilter, TileCompletionStatus, TileData, TileStatus } from '../model';
import type { ReloadedTileData, ReloadedTilesMap } from '../reloadedTiles/reloadedTiles.model';
import type { GroupColor } from '../tiles/tiles.model';
import { getRegulationData } from './dashboard.selectors.regulation';
import { _getTileData } from './dashboard.selectors.status';
import { _getTabsByQuoteId, getTabFromQuoteId } from './dashboard.selectors.tabs.helpers';
import { getUnderlyingsIndexableCodes } from './dashboard.selectors.underlyings.helpers';

function _getDashboardTilesFormData(state: AppState) {
  return state.formData.enriched;
}

function _getDashboardTilesMetadataMap(state: AppState) {
  return state.quotes.dashboard.tiles;
}

function _getDashboardFilterState(state: AppState) {
  return state.quotes.dashboard.filter;
}

function _getDashboardTilesOrder(state: AppState) {
  return state.quotes.dashboard.orderedTiles;
}

function _getInfosMap(state: AppState) {
  return state.quotes.infos;
}

export function isDashboardReloadedTileLoading(state: AppState) {
  return state.quotes.dashboard.reloaded.isLoading;
}

function _getDashboardReloadedTileMap(state: AppState) {
  return state.quotes.dashboard.reloaded.tiles;
}

function _getDetailsMap(state: AppState) {
  return state.quotes.details;
}

function _getResultMap(state: AppState) {
  return state.quotes.results;
}

function _getTradeInfosMap(state: AppState) {
  return state.quotes.trades;
}

export const getAllQuoteIdsUsedInDashboard = (state: AppState) => state.quotes.dashboard.orderedTiles;

export function getAllFilters(state: AppState) {
  return _getDashboardFilterState(state);
}

export const getSelectedFilter = createSelector([_getDashboardFilterState], (filterState): DashboardFilter => {
  switch (filterState.type) {
    case 'byStatus':
      return <DashboardFilter>{
        type: 'byStatus',
        status: filterState.status,
      };
    case 'byPinned':
      return <DashboardFilter>{
        type: 'byPinned',
        pinnedGroup: filterState.pinnedGroup,
      };
    case 'byClient':
      return <DashboardFilter>{
        type: 'byClient',
        client: filterState.client,
        product: filterState.product,
      };
  }
});

export function getQuickSearchTerm(state: AppState) {
  return _getDashboardFilterState(state).quickSearchTerm;
}

export const hasAssociatedTabId: (state: AppState, quoteId: string) => boolean = (state, quoteId) => {
  const tab = getTabFromQuoteId(state, quoteId);
  return tab !== undefined;
};

interface TermsheetParams {
  tabId: string;
  locales: ReadonlyArray<string>;
  quoteQuery: unknown;
}
export function getTermsheetParams(state: AppState, quoteId: string): TermsheetParams | undefined {
  const tab = getTabFromQuoteId(state, quoteId);
  if (tab) {
    const infos = getFormInfos(state, tab.workspace, tab.tabId);

    const quoteQuery = infos?.quoteQuery;
    const termsheet = getDocument(state, tab.workspace, tab.tabId, 'TermSheet' satisfies DocumentType);
    const locales: string[] = termsheet?.items?.filter(i => i.format === 'PDF').map(i => i.language) ?? [];

    return quoteQuery && locales.length > 0 ? { quoteQuery, tabId: tab.tabId, locales } : undefined;
  } else {
    return undefined;
  }
}

export function getInfos(state: AppState, quoteId: string): StaticFormInfos | DynamicFormInfos | undefined {
  const tab = getTabFromQuoteId(state, quoteId);
  return tab ? getFormInfos(state, tab.workspace, tab.tabId) : undefined;
}

export function getTileMetadata(state: AppState, quoteId: string) {
  const dashboardMap = _getDashboardTilesMetadataMap(state);
  return dashboardMap[quoteId];
}

const _getAllTilesData = createSelector(
  [
    _getDashboardTilesOrder,
    _getInfosMap,
    _getDetailsMap,
    _getResultMap,
    getPendingQuoteDetailsMap,
    _getTradeInfosMap,
    s => getIsUserInternal(s),
    _getTabsByQuoteId,
    _getDashboardReloadedTileMap,
    _getDashboardTilesFormData,
  ],
  (
    quoteIds,
    infosMap,
    detailsMap,
    resultMap,
    pendingDetailsMap,
    tradeInfosMap,
    isInternalUser,
    tabsMap,
    reloadedTileMap,
    formDatasMap,
  ): Map<string, TileData | undefined> => {
    const map = quoteIds.map(quoteId => {
      const infos = infosMap[quoteId];
      const details = detailsMap[quoteId];
      const result = resultMap[quoteId];
      const pendingDetails = pendingDetailsMap[quoteId];
      const tradeInfos = tradeInfosMap[quoteId];
      const reloadedTile = reloadedTileMap[quoteId];
      const isQuoteLive = (tabsMap.get(quoteId) ?? []).some(t => isLiveQuote(t.quoteState));
      const formData = formDatasMap[quoteId];

      const tileData = _getTileData(
        isInternalUser,
        infos,
        details,
        result,
        pendingDetails,
        tradeInfos,
        isQuoteLive,
        reloadedTile,
        formData,
      );
      return [quoteId, tileData] as [string, TileData | undefined];
    });
    return new Map<string, TileData | undefined>(map);
  },
);

export const getTileData: (state: AppState, quoteId: string) => TileData | undefined = (state, quoteId) =>
  _getAllTilesData(state).get(quoteId);

export const getTileFormData: (state: AppState, quoteId: string) => any | undefined = (state, quoteId) => {
  const tab = getTabFromQuoteId(state, quoteId);
  return _getDashboardTilesFormData(state)[tab?.tabId ?? ''];
};

export const canTileBeRepriced: (state: AppState, quoteId: string) => boolean = (state, quoteId) => {
  const tab = getTabFromQuoteId(state, quoteId);
  if (tab === undefined || tab.quoteState === undefined || tab.quoteState === 'Inconsistent') {
    return false;
  }
  const tileData = getTileData(state, quoteId);
  return _isTileStateReadyForReprice(tileData);
};

export const getTileStatus: (state: AppState, quoteId: string) => TileStatus | undefined = (state, quoteId) =>
  getTileData(state, quoteId)?.status;

export const buildDashboardPersistenceRequest = (state: AppState) => {
  const orderedQuoteIds = _getDashboardTilesOrder(state);
  const metadata = _getDashboardTilesMetadataMap(state);

  let tiles: ReloadedTilesMap = {};
  orderedQuoteIds.forEach(quoteId => {
    const tileData = getTileData(state, quoteId);
    const regulationData = getRegulationData(state, tileData?.status, quoteId);
    if (tileData !== undefined) {
      const data = { ...tileData, ...regulationData } as ReloadedTileData;
      tiles = addKey(tiles, quoteId, data);
    }
  });

  return {
    quoteIds: orderedQuoteIds,
    metadata,
    data: tiles,
  } as PersistencePayload;
};

function getTileCompletionStatus(data: TileData): TileCompletionStatus {
  switch (data.status) {
    case 'Quoted': {
      if (!data.isTradeable) {
        return 'completed';
      } else {
        return 'inprogress';
      }
    }
    case 'Traded':
    case 'Trading':
    case 'Tradeable':
      return 'completed';

    case 'RejectedWithPrice':
    case 'RejectedWithoutPrice':
    case 'TechnicalErrorWithPrice':
    case 'TechnicalErrorWithoutPrice':
      return 'rejected';

    default:
      return 'inprogress';
  }
}

function _isTileStateReadyForReprice(data: TileData | undefined): boolean {
  switch (data?.status) {
    case 'Quoted': {
      if (!data.isTradeable) {
        return true;
      } else {
        return false;
      }
    }
    case 'Tradeable':
    case 'TechnicalErrorWithPrice':
    case 'TechnicalErrorWithoutPrice':
    case 'RejectedWithPrice':
    case 'RejectedWithoutPrice':
    case 'Traded':
      return true;

    default:
      return false;
  }
}

///
export interface QuoteIndexableData {
  quoteId: string;
  isPinned: boolean;
  completionStatus: TileCompletionStatus;
  quoteExternalId?: string;
  clientName?: string;
  clientId?: string;
  product: string;
  subProduct?: string;
  customGroup?: string;
  wrapper?: string;
  solveMode?: SolvingModeDetail;
  underlyings: string[];
  maturityDate: string;
  tenor: number;
  tenorUnit: string;
  currency: string;
}
export const getAllQuotesIndexes = createSelector(
  [_getAllTilesData, _getDashboardTilesOrder, _getDashboardTilesMetadataMap],
  (tilesDataMap, tilesOrder, tilesMetadataMap): QuoteIndexableData[] => {
    return tilesOrder
      .map(quoteId => {
        const tileData = tilesDataMap.get(quoteId);

        const isPinned = tilesMetadataMap[quoteId]?.isPinned ?? false;
        if (isNotDefined(tileData)) {
          return undefined;
        }
        const quoteExternalId = tileData.externalQuoteId;

        const underlyings = getUnderlyingsIndexableCodes(tileData.underlyings);
        const solveMode = tileData.solveMode;
        const product = tileData.product;
        const subProduct = tileData.subProduct;
        const customGroup = tilesMetadataMap[quoteId]?.groupName;
        const wrapper = tileData.wrapper;
        const clientName = tileData.clientName;
        const clientId = tileData.clientId;
        const maturityDate = tileData.maturityDate;
        const tenor = tileData.tenor?.value;
        const tenorUnit = tileData.tenor?.unit;
        const currency = tileData.currency;

        const completionStatus = getTileCompletionStatus(tileData);

        return {
          quoteId,
          isPinned,
          customGroup,
          completionStatus,
          quoteExternalId,
          clientName,
          clientId,
          product,
          subProduct,
          wrapper,
          solveMode,
          underlyings,
          maturityDate,
          tenor,
          tenorUnit,
          currency,
        };
      })
      .filter(isDefined);
  },
);

export interface PinnedGroup {
  name: string;
  color?: GroupColor;
}

/** @todo check if this can be done more efficiently */
export const getCustomGroups = createSelector([_getDashboardTilesMetadataMap], (tilesMetadataMap): PinnedGroup[] =>
  Object.entries(
    Object.values(tilesMetadataMap).reduce<Record<string, GroupColor | undefined>>((map, tileMetadata) => {
      if (tileMetadata?.groupName) {
        map[tileMetadata.groupName] = tileMetadata.groupColor;
      }
      return map;
    }, {}),
  )
    .map(([name, color]) => ({
      name,
      color,
    }))
    .sort((group1, group2) => group1.name!.localeCompare(group2.name!)),
);
